import { useCallback, useEffect, useRef, useState } from "react";
import { readServerReadiness } from "../api/healthApi";
import { isAbortError } from "../api/http/httpClient";
import { reportUiError, toUiError } from "../errors/uiError";
import { connectionAfterFailedReadinessCheck, type ServerConnection } from "./serverConnection";

/** How often the readiness check repeats while the app is open. */
const READINESS_CHECK_INTERVAL_MILLISECONDS = 30_000;

/** Check the server's readiness now, every 30 seconds, and whenever the window regains focus. */
export function useServerConnection(): { connection: ServerConnection; checkNow: () => void } {
  const [connection, setConnection] = useState<ServerConnection>({ status: "checking" });
  const runningCheckRef = useRef<AbortController | null>(null);

  const checkNow = useCallback(() => {
    runningCheckRef.current?.abort();
    const controller = new AbortController();
    runningCheckRef.current = controller;
    readServerReadiness(controller.signal).then(
      () => {
        setConnection({ status: "connected" });
      },
      (caught: unknown) => {
        if (isAbortError(caught)) {
          return;
        }
        const error = toUiError(caught);
        reportUiError(error);
        setConnection(connectionAfterFailedReadinessCheck(error));
      },
    );
  }, []);

  useEffect(() => {
    checkNow();
    const interval = window.setInterval(checkNow, READINESS_CHECK_INTERVAL_MILLISECONDS);
    window.addEventListener("focus", checkNow);
    window.addEventListener("online", checkNow);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", checkNow);
      window.removeEventListener("online", checkNow);
      runningCheckRef.current?.abort();
    };
  }, [checkNow]);

  return { connection, checkNow };
}
